import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MotifIcon,
  MotifButton,
  MotifCard,
  MotifCardHeader,
  MotifCardBody,
  MotifCardFooter,
  MotifFormField,
  MotifSelect,
  MotifOption,
  MotifLabel,
  MotifProgressLoader,
  MotifErrorMessage,
  MotifToast,
} from "@ey-xd/motif-react";
import { IconoirArrowLeft } from "@ey-xd/motif-icon";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import {
  useReadSchemeQuery,
  useListSchemeVersionsQuery,
  useLazyReadSchemeProcessIngStatusQuery,
} from "@/services/scheme/schemeReducer";
import { useGetClassListQuery } from "@/services/class/classReducer";
import { useGetRulesListQuery } from "@/services/rules/rulesReducer";
import Class from "./Class/Class";
import Rules from "./Rules/Rules";
import "./Schemes.scss";
import EditScheme from "./EditScheme";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import { useDispatch, useSelector } from "react-redux";
import { commonFormateName, havingEditPermission } from "@/utility/common";
import { EDIT_BUTTON_VARIANT } from "@/components/Images/CommonImages";
import Banner, { BannerElement } from "@/components/Banner/Banner";
import SchemeEffectivePopup from "./processing/SchemeEffectivePopup";
import { ActiveTabsOrder } from "@/menuData";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
import { useLazyExportDocumentQuery } from "@/services/exports/exportsReducer";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";

const SchemeManage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseName } = useSelector((state: any) => state.case);
  const retainState = useSelector(
    (e: any) => e.retainState.schemeSummaryTabState
  );
  const [exportDocument, { isFetching: exportingScheme }] =
    useLazyExportDocumentQuery();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [schemeVersion, seSchemeVersion] = useState(
    retainState.schemeVersionId || ""
  );
  const dispatch = useDispatch();
  // const [isPending, setIsPending] = useState(false);
  const [isMakeSchemeDisabled, setIsMakeSchemeDisabled] = useState(false);
  const [showSchemeProcessing, setShowSchemeProcessing] = useState(false);
  const [show, setShow] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [isDraft, setIsDraft] = useState(retainState.isDraft || false);
  const [fileDownloadStatus, setFileDownloadStatus] = useState("");
  const { caseId, schemeId } = useParams<{
    caseId: string;
    schemeId: string;
  }>();
  const { data, refetch } = useReadSchemeQuery(schemeId ?? "");
  const {
    data: schemeVersions,
    isLoading: isListSchemeVersionLoading,
    refetch: schemeVersionRefetch,
    error: schemeVersionError,
  } = useListSchemeVersionsQuery(schemeId ?? "");
  const [getProcessingStatus, { data: processingData }] =
    useLazyReadSchemeProcessIngStatusQuery();
  const {
    data: initialClassData,
    refetch: classRefetch,
    isLoading: isClassListLoading,
    error: classError,
  } = useGetClassListQuery(schemeVersion, {
    skip: !schemeVersion,
  });
  const {
    data: initialRulesData,
    refetch: RuleRefetch,
    isLoading: isRuleListLoading,
    error: ruleError,
  } = useGetRulesListQuery(schemeVersion, {
    skip: !schemeVersion,
  });
  const { caseShortName, abbreviation } = useSelector(
    (state: any) => state.case
  );
  const schemeDetails = [
    {
      title: t("pages.configuration.scheme.formLabels.name"),
      value: data?.name,
    },
    {
      title: t("pages.configuration.scheme.formLabels.description"),
      value: data?.description,
    },
    {
      title: t("pages.configuration.scheme.formLabels.nameFrench"),
      value: data?.nameFrench,
    },
    {
      title: t("pages.configuration.scheme.formLabels.descriptionFrench"),
      value: data?.descriptionFrench,
    },
  ];
  const permissions = useSelector((state: any) => state.permissions);
  const { Scheme, SchemeVersion, Class: classPermissions, Rule } = permissions;
  useEffect(() => {
    if (schemeVersions?.length && !schemeVersion) {
      const draftScheme = schemeVersions.find(
        (version: any) => version.name === "Current Draft Scheme"
      );
      if (draftScheme) {
        setIsDraft(draftScheme.isDraft);
        seSchemeVersion(draftScheme.schemeVersionId);
      }
    }
  }, [schemeVersions, dispatch, schemeVersion]);

  useEffect(() => {
    if (schemeVersion) {
      setStatusMsg("");
      setErrorMessage("");
      // setIsPending(false);
      getProcessingStatus(schemeVersion).then((response: any) => {
        getSchemeStatus(response);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemeVersion]);
  function getSchemeStatus(response: any) {
    if (response) {
      const processingStatus = response?.data?.processingStatus;
      if (processingStatus === 1 || processingStatus === 2) {
        // setIsPending(true);
        window.scrollTo(0, 0);
        setStatusMsg(t("pages.configuration.scheme.schemeProcessingMessage"));
        setErrorMessage("");
      } else if (processingStatus === 3) {
        setErrorMessage(
          t("pages.configuration.scheme.schemeProcessingErrorMessage", {
            errorMessage: response?.data?.processingStatusMessage,
          })
        );
        setStatusMsg("");
      } else {
        // setIsPending(false);
        setStatusMsg("");
        setErrorMessage("");
      }
    } else {
      // setIsPending(false);
      setStatusMsg("");
      setErrorMessage("");
    }
  }

  useEffect(() => {
    if (schemeVersion) {
      RuleRefetch();
    }
  }, [schemeVersion, RuleRefetch]);

  const handleSchemeVersionChange = (value: string) => {
    const selectedVersion = schemeVersions?.find(
      (version: any) => version.schemeVersionId === value
    );
    if (selectedVersion) {
      setIsDraft(selectedVersion.isDraft);
    }
    seSchemeVersion(value);
    dispatch(
      setComponentState1({
        entityName: "schemeSummaryTabState",
        entityState: {
          schemeVersionId: value,
          isDraft: selectedVersion?.isDraft,
        },
      })
    );
  };

  if (isListSchemeVersionLoading) {
    return <MotifProgressLoader show variant="default" />;
  }
  if (schemeVersionError) {
    return (
      <MotifErrorMessage>
        {t(
          "pages.configuration.scheme.validationMessages.schemeVersionMessage"
        )}
      </MotifErrorMessage>
    );
  }

  const handlePreviewClick = () => {
    navigate(
      `/case/${caseId}/scheme-manage/${schemeId}/preview/${schemeVersion}`,
      {
        state: { schemeVersion },
      }
    );
  };

  const bannerElements: BannerElement[] = [
    {
      label: "pages.configuration.scheme.schemeBanner.lastEffectiveDate",
      value: data?.effectiveDate || "-",
    },
  ];

  return (
    <>
      <div className="scheme-manage-container">
        <MotifCard variant="card-no-border">
          <MotifCardHeader style={{ display: "block", paddingBottom: "0" }}>
            {data?.name && caseName ? (
              <Breadcrumb
                items={[
                  {
                    text: caseName,
                    url: `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`,
                    icon: <MotifIcon src={IconoirArrowLeft} />,
                  },
                  {
                    text: data?.name,
                    url: "/",
                    icon: <MotifIcon src={IconoirArrowLeft} />,
                    disabled: true,
                  },
                ]}
                ariaLabel="Breadcrumb navigation"
              />
            ) : (
              <MotifProgressLoader variant="default"></MotifProgressLoader>
            )}
            <Banner
              bannerName="pages.configuration.scheme.schemeBanner.bannerTitle"
              bannerNameValue={data?.name}
              elements={bannerElements}
            />
          </MotifCardHeader>
          <MotifCardBody>
            {statusMsg && (
              <MotifToast
                onClose={() => {
                  setStatusMsg("");
                }}
                //@ts-ignore
                variant={"info"}
                supportingText={
                  processingData?.processingStatusMessage &&
                  [1, 2].includes(processingData?.processingStatus)
                    ? t("global.globalNotifications.progressingMessage", {
                        status: processingData?.processingStatusMessage,
                      })
                    : undefined
                }
              >
                {" "}
                {t(statusMsg)}
              </MotifToast>
            )}
            {errorMessage && (
              <NotificationGrid
                show={true}
                message={errorMessage}
                type={"error"}
                onClose={() => setErrorMessage("")}
              />
            )}
            {fileDownloadStatus && (
              <NotificationGrid
                show={true}
                message={fileDownloadStatus}
                type={"error"}
                onClose={() => setFileDownloadStatus("")}
              />
            )}
            <div className="motif-container scheme-summary-section pad-v-10 mar-t-20">
              <div className="motif-row justify-content-between align-items-center">
                <div className="motif-col-lg-11">
                  <div className="motif-body1-default-regular summary-title">
                    {t("pages.configuration.scheme.schemeDetails")}
                  </div>
                  <div className="motif-body2-default-regular summary-description">
                    {t("pages.configuration.scheme.detailDescription")}
                  </div>
                </div>
                <div className="motif-col-lg-1">
                  {havingEditPermission(Scheme) && (
                    <MotifButton
                      onClick={() => setShow(true)}
                      size="small"
                      type="submit"
                      variant={EDIT_BUTTON_VARIANT}
                    >
                      {t("pages.configuration.scheme.edit")}
                    </MotifButton>
                  )}
                </div>
              </div>
            </div>
            <div className="motif-container scheme-summary-details pad-v-20">
              <div className="motif-row">
                {schemeDetails.map((detail, colIndex) => (
                  <div key={colIndex} className="motif-col-lg-3">
                    <div>
                      <div className="title">{detail.title}</div>
                      <div className="motif-body2-default-bold overFlowWrapAnywhere">
                        {detail.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="motif-container manage-section ">
              <div className="motif-row justify-content-between align-items-center">
                <div>
                  <div className="motif-h5-default-regular margin-title">
                    {t("pages.configuration.scheme.manageRuleAndClass")}
                  </div>
                  <div className="motif-body2-default-regular margin-description">
                    {t(
                      "pages.configuration.scheme.manageRulesAndClassDescription"
                    )}
                  </div>
                </div>
                <div>
                  <div className="preview-div">
                    <div className="preview-button">
                      <MotifButton
                        onClick={handlePreviewClick}
                        size="small"
                        type="submit"
                        variant="secondary"
                        disabled={!initialRulesData?.length}
                      >
                        {t("pages.configuration.scheme.preview")}
                      </MotifButton>
                    </div>
                    <MotifFormField style={{minWidth:"200px"}} className="manage-type-select">
                      <MotifLabel>
                        {t("pages.configuration.scheme.viewVersion")}
                      </MotifLabel>
                      <MotifSelect
                        onChange={(value) => handleSchemeVersionChange(value)}
                        value={schemeVersion}
                      >
                        {schemeVersions?.map((version: any) => (
                          <MotifOption
                            key={version.schemeVersionId}
                            value={version.schemeVersionId}
                          >
                            {version.name}
                          </MotifOption>
                        ))}
                      </MotifSelect>
                    </MotifFormField>
                  </div>
                </div>
              </div>
            </div>
            {schemeVersion && (
              <>
                {isClassListLoading ? (
                  <MotifProgressLoader show variant="default" />
                ) : classError ? (
                  <MotifErrorMessage>
                    {t("global.globalNotifications.wentWrong")}
                  </MotifErrorMessage>
                ) : (
                  <Class
                    schemeVersionId={schemeVersion}
                    initialClassData={initialClassData}
                    classRefetch={classRefetch}
                    isDraft={isDraft}
                    classPermissions={classPermissions}
                  />
                )}

                {isRuleListLoading ? (
                  <MotifProgressLoader show variant="default" />
                ) : ruleError ? (
                  <MotifErrorMessage>
                    {t("global.globalNotifications.wentWrong")}
                  </MotifErrorMessage>
                ) : (
                  <Rules
                    initialRulesData={initialRulesData}
                    ruleRefetch={RuleRefetch}
                    classRefetch={classRefetch}
                    isDraft={isDraft}
                    schemeName={data?.name}
                    Rule={Rule}
                  />
                )}
              </>
            )}
          </MotifCardBody>
          <MotifCardFooter className="sticky-footer">
            {havingEditPermission(SchemeVersion) && (
              <div className="manage-footer-buttons">
                <MotifButton
                  size="small"
                  variant="primary"
                  type={"button"}
                  loading={exportingScheme}
                  onClick={() => {
                    setFileDownloadStatus("");
                    exportDocument({
                      schemeId,
                      url: "/Scheme/ExportClaimItemClass",
                      fileName: caseShortName
                        ? commonFormateName(
                            caseShortName,
                            data?.name,
                            abbreviation,
                            "Scheme"
                          )
                        : "",
                    })
                      .then((response) => {
                        if (response?.error && "data" in response.error) {
                          try {
                            const errObj = JSON.parse(
                              response?.error?.data as string
                            );
                            setFileDownloadStatus(
                              errObj?.message || errObj.title
                            );
                          } catch (error) {
                            setFileDownloadStatus("Something went wrong!!");
                            console.error("File download error:", error);
                          }
                        }
                      })
                      .catch((error: any) => {
                        setFileDownloadStatus("Something went wrong!!");
                        console.error("File download error:", error);
                      });
                  }}
                >
                  {t("pages.configuration.scheme.generateSchemeReport")}
                </MotifButton>
                <MotifButton
                  size="small"
                  variant="primary"
                  type={"button"}
                  disabled={
                    !processingData ||
                    isMakeSchemeDisabled ||
                    !initialClassData?.length
                  }
                  onClick={() => {
                    if (![1, 2]?.includes(processingData.processingStatus)) {
                      setStatusMsg("");
                      setErrorMessage("");
                      setFileDownloadStatus("");
                      setShowSchemeProcessing(true);
                    } else {
                      setShowConfirmationModal(true);
                    }
                    setIsMakeSchemeDisabled(true);
                  }}
                >
                  {t("pages.configuration.scheme.schemeEffective")}
                </MotifButton>
              </div>
            )}
          </MotifCardFooter>
        </MotifCard>
      </div>
      {show && (
        <EditScheme
          show={show}
          setShow={setShow}
          editData={data}
          refetch={refetch}
        />
      )}
      {showSchemeProcessing && (
        <SchemeEffectivePopup
          showModal={showSchemeProcessing}
          schemeVersionId={schemeVersion}
          schemeName={data?.name}
          schemeId={schemeId}
          abbreviation={abbreviation}
          caseShortName={caseShortName}
          schemeVersionRefetch={schemeVersionRefetch}
          setStatusMsg={setStatusMsg}
          onClose={() => {
            setShowSchemeProcessing(false);
            setIsMakeSchemeDisabled(false);
          }}
          setFileDownloadStatus={setFileDownloadStatus}
          getSchemeStatus={getSchemeStatus}
        />
      )}
      <ConfirmationModal
        isVisible={showConfirmationModal}
        setIsVisible={() => {}}
        cancelBtnApproveHandler={() => {
          setIsMakeSchemeDisabled(false);
          setShowSchemeProcessing(true);
          setShowConfirmationModal(false);
          setStatusMsg("");
          setErrorMessage("");
          setFileDownloadStatus("");
        }}
        cancelBtnRejectHandler={() => {
          setIsMakeSchemeDisabled(false);
          setShowConfirmationModal(false);
        }}
        showHeader={false}
        type={3}
        message={t(
          "pages.configuration.scheme.validationMessages.schemeEffectiveWarningMessage"
        )}
      ></ConfirmationModal>
    </>
  );
};
export default SchemeManage;
